/**
 * Input Manager - handles pointer and keyboard input
 */

import { EventBus } from '@/core/events/EventBus';

export interface InputState {
  pointerDown: boolean;
  pointerButton: number;
  pointerX: number;
  pointerY: number;
  lastPointerX: number;
  lastPointerY: number;
  dragStartX: number;
  dragStartY: number;
  isDragging: boolean;
  keys: Set<string>;
}

export class InputManager {
  private state: InputState = {
    pointerDown: false,
    pointerButton: 0,
    pointerX: 0,
    pointerY: 0,
    lastPointerX: 0,
    lastPointerY: 0,
    dragStartX: 0,
    dragStartY: 0,
    isDragging: false,
    keys: new Set(),
  };

  private element: HTMLElement;
  private onPointerDownBound: (e: PointerEvent) => void;
  private onPointerUpBound: (e: PointerEvent) => void;
  private onPointerMoveBound: (e: PointerEvent) => void;
  private onWheelBound: (e: WheelEvent) => void;
  private onKeyDownBound: (e: KeyboardEvent) => void;
  private onKeyUpBound: (e: KeyboardEvent) => void;

  public onPan?: (dx: number, dy: number) => void;
  public onZoom?: (delta: number) => void;
  public onClick?: (x: number, y: number, button: number) => void;
  public onRightClick?: (x: number, y: number) => void;
  public onDragStart?: (x: number, y: number, button: number) => void;
  public onDragMove?: (x: number, y: number) => void;
  public onDragEnd?: () => void;

  constructor(element: HTMLElement) {
    this.element = element;

    this.onPointerDownBound = this.onPointerDown.bind(this);
    this.onPointerUpBound = this.onPointerUp.bind(this);
    this.onPointerMoveBound = this.onPointerMove.bind(this);
    this.onWheelBound = this.onWheel.bind(this);
    this.onKeyDownBound = this.onKeyDown.bind(this);
    this.onKeyUpBound = this.onKeyUp.bind(this);

    element.addEventListener('pointerdown', this.onPointerDownBound);
    element.addEventListener('pointerup', this.onPointerUpBound);
    element.addEventListener('pointermove', this.onPointerMoveBound);
    element.addEventListener('wheel', this.onWheelBound, { passive: false });
    element.addEventListener('contextmenu', (e) => e.preventDefault());
    window.addEventListener('keydown', this.onKeyDownBound);
    window.addEventListener('keyup', this.onKeyUpBound);
  }

  private onPointerDown(e: PointerEvent): void {
    this.state.pointerDown = true;
    this.state.pointerButton = e.button;
    this.state.lastPointerX = e.clientX;
    this.state.lastPointerY = e.clientY;
    this.state.dragStartX = e.clientX;
    this.state.dragStartY = e.clientY;
    this.state.isDragging = false;
    this.element.setPointerCapture(e.pointerId);
  }

  private onPointerUp(e: PointerEvent): void {
    const wasDragging = this.state.isDragging;
    const dx = Math.abs(e.clientX - this.state.dragStartX);
    const dy = Math.abs(e.clientY - this.state.dragStartY);

    this.state.pointerDown = false;
    this.element.releasePointerCapture(e.pointerId);

    // If was box selecting (left button drag), end the selection
    if (wasDragging && this.state.pointerButton === 0) {
      this.onDragEnd?.();
    }

    // If the pointer didn't move much, treat as a click
    if (dx < 5 && dy < 5) {
      if (e.button === 2) {
        this.onRightClick?.(e.clientX, e.clientY);
      } else {
        this.onClick?.(e.clientX, e.clientY, e.button);
      }
      EventBus.emit('input:click', {
        worldX: e.clientX,
        worldZ: e.clientY,
        button: e.button,
      });
    }

    this.state.isDragging = false;
  }

  private onPointerMove(e: PointerEvent): void {
    this.state.pointerX = e.clientX;
    this.state.pointerY = e.clientY;

    if (!this.state.pointerDown) return;

    const dx = e.clientX - this.state.lastPointerX;
    const dy = e.clientY - this.state.lastPointerY;
    const totalDx = Math.abs(e.clientX - this.state.dragStartX);
    const totalDy = Math.abs(e.clientY - this.state.dragStartY);

    this.state.lastPointerX = e.clientX;
    this.state.lastPointerY = e.clientY;

    // Start dragging if moved more than 5 pixels
    if (!this.state.isDragging && (totalDx > 5 || totalDy > 5)) {
      this.state.isDragging = true;
      // Only trigger drag start for left button (box selection)
      if (this.state.pointerButton === 0) {
        this.onDragStart?.(this.state.dragStartX, this.state.dragStartY, this.state.pointerButton);
      }
    }

    // If dragging with left button, update box selection
    if (this.state.isDragging && this.state.pointerButton === 0) {
      this.onDragMove?.(e.clientX, e.clientY);
    }

    // Middle button or right button for panning
    if (this.state.pointerButton === 1 || this.state.pointerButton === 2) {
      this.onPan?.(dx, dy);
    }
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 1 : -1;
    this.onZoom?.(delta);
  }

  private onKeyDown(e: KeyboardEvent): void {
    this.state.keys.add(e.code);
  }

  private onKeyUp(e: KeyboardEvent): void {
    this.state.keys.delete(e.code);
  }

  isKeyDown(code: string): boolean {
    return this.state.keys.has(code);
  }

  getPointerPosition(): { x: number; y: number } {
    return { x: this.state.pointerX, y: this.state.pointerY };
  }

  dispose(): void {
    this.element.removeEventListener('pointerdown', this.onPointerDownBound);
    this.element.removeEventListener('pointerup', this.onPointerUpBound);
    this.element.removeEventListener('pointermove', this.onPointerMoveBound);
    this.element.removeEventListener('wheel', this.onWheelBound);
    window.removeEventListener('keydown', this.onKeyDownBound);
    window.removeEventListener('keyup', this.onKeyUpBound);
  }
}
